import type { QueryClient } from '@tanstack/react-query';

export type TipoLancamento = 'entrada' | 'saida' | 'transferencia';
export type TipoConta = 'banco' | 'caixa' | 'cartao' | 'investimento' | 'poupanca';
/** Fase 2, W24 (K9, B1): ordem do seletor de tipo de conta. */
export const TIPOS_CONTA: TipoConta[] = ['banco', 'caixa', 'cartao', 'investimento', 'poupanca'];
export type TipoCategoria = 'receita' | 'despesa';

export interface Lancamento {
  id: string;
  /** C2: com uma conta só no filtro, o saldo da conta depois deste lançamento. */
  saldo_acumulado_centavos?: number;
  tipo: TipoLancamento;
  conta_id: string;
  conta_nome: string;
  conta_destino_id: string | null;
  conta_destino_nome: string | null;
  categoria_id: string | null;
  categoria_nome: string | null;
  centro_custo_id: string | null;
  centro_custo_nome: string | null;
  parceiro_id: string | null;
  parceiro_nome: string | null;
  descricao: string;
  valor_centavos: number;
  moeda: string;
  data_competencia: string;
  data_caixa: string;
  comprovante_anexo_id: string | null;
  conciliado: boolean;
  estorno_de: string | null;
  estornado_por: string | null;
  origem: { modulo: string; entidade: string; id: string } | null;
  extras: Record<string, unknown>;
  created_at: string;
}

export interface Totais {
  quantidade: number;
  entradas_centavos: number;
  saidas_centavos: number;
  transferencias_centavos: number;
  /** C2: quantidade de transferências (frase do card Transferências). */
  transferencias: number;
  saldo_centavos: number;
}

export interface Conta {
  id: string;
  nome: string;
  tipo: TipoConta;
  banco: string | null;
  agencia: string | null;
  conta: string | null;
  saldo_inicial_centavos: number;
  saldo_centavos: number;
  moeda: string;
  ativa: boolean;
  padrao: boolean;
}

export type Natureza = 'receita' | 'despesa' | 'ativo' | 'passivo' | 'neutra';
export type GrupoDre = 'receita_bruta' | 'deducoes' | 'custos' | 'despesas_operacionais' | 'resultado_financeiro' | 'nao_operacional' | 'fora_dre';
export type Papel = 'juros_recebidos' | 'desconto_concedido' | 'juros_pagos' | 'desconto_obtido' | 'perda_credito' | 'tarifa_bancaria';
export const NATUREZAS: Natureza[] = ['receita', 'despesa', 'ativo', 'passivo', 'neutra'];
export const GRUPOS_DRE: GrupoDre[] = ['receita_bruta', 'deducoes', 'custos', 'despesas_operacionais', 'resultado_financeiro', 'nao_operacional', 'fora_dre'];
/** Uso automático de cada tipo de categoria (fase 2, W7). */
export const PAPEIS_DO_TIPO: Record<TipoCategoria, Papel[]> = { receita: ['juros_recebidos', 'desconto_concedido'], despesa: ['juros_pagos', 'desconto_obtido', 'perda_credito', 'tarifa_bancaria'] };

export interface Categoria {
  id: string;
  nome: string;
  tipo: TipoCategoria;
  codigo: string | null;
  parent_id: string | null;
  ativa: boolean;
  lancamentos: number;
  /** Fase 2, W7: o que está gravado (vazio herda) e o efetivo (herdado do pai). */
  natureza?: Natureza | null;
  grupo_dre?: GrupoDre | null;
  papel?: Papel | null;
  natureza_efetiva?: Natureza | null;
  grupo_dre_efetivo?: GrupoDre | null;
  /** Fase 2, W24 (K9): sintética só agrupa (não recebe lançamento). */
  sintetica?: boolean;
}

export interface LinhaExtratoConta {
  id: string;
  data_caixa: string;
  tipo: TipoLancamento;
  descricao: string;
  efeito_centavos: number;
  saldo_centavos: number;
  conciliado: boolean;
  estorno_de: string | null;
  estornado_por: string | null;
  categoria_id: string | null;
  categoria_nome: string | null;
  outra_conta_id: string | null;
  outra_conta_nome: string | null;
  /** Fase 2, W24 (K9, P4): forma de pagamento (a da baixa do Contas). */
  forma_pagamento_nome?: string | null;
}

export interface ExtratoConta {
  conta_id: string;
  conta_nome: string;
  moeda: string;
  de: string;
  ate: string;
  saldo_anterior_centavos: number;
  entradas_centavos: number;
  saidas_centavos: number;
  saldo_final_centavos: number;
  linhas: LinhaExtratoConta[];
  total_linhas: number;
  truncado: boolean;
}

export interface CentroCusto {
  id: string;
  nome: string;
  codigo: string | null;
  ativo: boolean;
  lancamentos: number;
}

export interface LinhaExtrato {
  id: string;
  extrato_id: string;
  conta_id: string;
  conta_nome: string;
  data: string;
  descricao: string;
  valor_centavos: number;
  id_banco: string;
  lancamento_id: string | null;
  lancamento_descricao: string | null;
  conciliada_em: string | null;
  sugestoes: number;
}

export interface Sugestao {
  id: string;
  tipo: TipoLancamento;
  descricao: string;
  valor_centavos: number;
  moeda: string;
  data_caixa: string;
  conta_nome: string;
  categoria_nome: string | null;
  parceiro_nome: string | null;
  diferenca_dias: number;
}

export interface PeriodoFluxo {
  inicio: string;
  fim: string;
  realizado_entradas_centavos: number;
  realizado_saidas_centavos: number;
  previsto_entradas_centavos: number;
  previsto_saidas_centavos: number;
  saldo_projetado_centavos: number;
}

export interface FluxoCaixa {
  de: string;
  ate: string;
  hoje: string;
  agrupar: 'dia' | 'semana' | 'mes';
  conta_id: string | null;
  abertura_centavos: number;
  periodos: PeriodoFluxo[];
  totais: { realizado_entradas_centavos: number; realizado_saidas_centavos: number; previsto_entradas_centavos: number; previsto_saidas_centavos: number };
  previsto_com_titulos: boolean;
}

export interface Pagina<T> {
  itens: T[];
  proximo_cursor: string | null;
}

/** Depois de qualquer escrita: lista, totais, saldos e fluxo mudam juntos. */
export function invalidarFinanceiro(qc: QueryClient) {
  return qc.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith('/financeiro') });
}

/** Hoje no relógio do navegador (AAAA-MM-DD), sem o desvio de UTC que a noite brasileira causaria. */
export function hojeLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Cor do valor: entrada verde, saída vermelha, transferência neutra. */
export function corDoValor(tipo: TipoLancamento) {
  return tipo === 'entrada' ? 'text-verde' : tipo === 'saida' ? 'text-perigo' : 'text-tinta-1';
}
